/**
 * ContactInbox — review queue for Gmail-harvested contact candidates.
 *
 * Candidates are staged by the GAS Contact Radar harvester; nothing enters
 * the CRM without an approval here. The queue UI itself is the shared
 * ContactInboxList (@techstack/ui, audit CRM-3) — the same component the GAS
 * cockpit renders, so the two surfaces can't drift into opposite semantics
 * again. This host owns transport (apiClient → workspace routes, react-query
 * cache) and the notice/undo banner.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContactInboxList } from '@techstack/ui';
import '@techstack/ui/dist/styles/contact-inbox.css';
import apiClient from '../../lib/apiClient';
import './ContactInbox.css';

// Banner dwell times. An Undo needs longer than a plain confirmation, and
// errors wait for the user — they're the only thing worth reading twice.
const NOTICE_PLAIN_MS = 5000;
const NOTICE_UNDO_MS = 7000;

export default function ContactInbox() {
  const queryClient = useQueryClient();
  // notice: string | { text, undoIds } — undoIds renders an Undo button that
  // restores just-rejected candidates (rejection doubles as the harvest
  // suppression list, so it must be one click to recover). Successes and
  // undo offers clear themselves; only errors stay (2026-08-14 — the undo
  // banner used to sit there until the next action replaced it).
  const [notice, setNotice] = useState(null);
  const noticeTimer = useRef(null);
  const showNotice = useCallback((value, { sticky = false } = {}) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    setNotice(value);
    if (value == null || sticky) return;
    const ms = typeof value === 'object' && value.undoIds ? NOTICE_UNDO_MS : NOTICE_PLAIN_MS;
    noticeTimer.current = setTimeout(() => setNotice(null), ms);
  }, []);
  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);
  // Rejections arrive in bursts; they collapse into one banner whose Undo
  // restores the whole burst instead of one banner per click.
  const rejectBatch = useRef({ ids: [], at: 0 });
  const [rejected, setRejected] = useState(null); // null = not loaded yet
  // Signal-tier filter. Server-side (the list is capped), so it's part of
  // the query key rather than a client-side filter over one page.
  const [tier, setTier] = useState('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['contact-candidates', 'pending', tier],
    queryFn: () => apiClient.get(
      `/api/workspace/contact-candidates?status=pending${tier !== 'all' ? `&tier=${tier}` : ''}`
    ),
    staleTime: 30_000,
  });

  const { data: accountsData } = useQuery({
    queryKey: ['workspace-accounts'],
    queryFn: () => apiClient.get('/api/workspace/accounts'),
    staleTime: 300_000,
  });
  const accounts = accountsData?.accounts || [];

  const loadRejected = async () => {
    try {
      const r = await apiClient.get('/api/workspace/contact-candidates?status=rejected');
      setRejected(r?.candidates || []);
    } catch {
      setRejected([]);
    }
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['contact-candidates'] });
    queryClient.invalidateQueries({ queryKey: ['workspace'] });
    if (rejected !== null) loadRejected();
  };

  // Optimistic queue (UI-responsiveness review 2026-08-08): approve/reject
  // drops the row the instant it's clicked — this is a rapid-fire triage
  // surface. onMutate snapshots the pending cache and rolls back on error;
  // the refresh on settle reconciles counts and the rejected shelf.
  const pendingKey = ['contact-candidates', 'pending', tier];
  const dropPending = async (ids) => {
    const set = new Set(Array.isArray(ids) ? ids : [ids]);
    await queryClient.cancelQueries({ queryKey: pendingKey });
    const previous = queryClient.getQueryData(pendingKey);
    queryClient.setQueryData(pendingKey, (prev) => {
      if (!prev || !Array.isArray(prev.candidates)) return prev;
      const candidates = prev.candidates.filter((c) => !set.has(c.id));
      const removed = prev.candidates.length - candidates.length;
      return {
        ...prev,
        candidates,
        total: Math.max(0, (prev.total ?? prev.candidates.length) - removed),
        count: Math.max(0, (prev.count ?? prev.candidates.length) - removed),
      };
    });
    return { previous };
  };
  const restorePending = (_e, _v, ctx) => {
    if (ctx?.previous) queryClient.setQueryData(pendingKey, ctx.previous);
  };

  const approve = useMutation({
    mutationFn: ({ id, overrides }) =>
      apiClient.post(`/api/workspace/contact-candidates/${id}/approve`, overrides || {}),
    onMutate: ({ id }) => dropPending(id),
    onSuccess: () => showNotice(null),
    onError: (e, v, ctx) => { restorePending(e, v, ctx); showNotice(`Approve failed: ${e.message}`, { sticky: true }); },
    onSettled: refresh,
  });

  const approveBulk = useMutation({
    // entries: bare ids or { id, first_name?, last_name?, coverage_account_ids? }
    // — the route honors per-row edits (audit CRM-2).
    mutationFn: (entries) => apiClient.post('/api/workspace/contact-candidates/approve-bulk', { ids: entries }),
    onMutate: (entries) => dropPending(entries.map((e) => (e && typeof e === 'object' ? e.id : e))),
    onSuccess: (r) => {
      showNotice(r.skipped?.length ? `${r.approved} approved, ${r.skipped.length} skipped` : null);
    },
    onError: (e, v, ctx) => { restorePending(e, v, ctx); showNotice(`Bulk approve failed: ${e.message}`, { sticky: true }); },
    onSettled: refresh,
  });

  const reject = useMutation({
    mutationFn: (id) => apiClient.post(`/api/workspace/contact-candidates/${id}/reject`),
    onMutate: (id) => dropPending(id),
    onSuccess: (_r, id) => {
      const now = Date.now();
      const batch = rejectBatch.current;
      if (now - batch.at > NOTICE_UNDO_MS) batch.ids = [];
      batch.ids.push(id);
      batch.at = now;
      const ids = batch.ids.slice();
      showNotice({
        text: ids.length === 1
          ? 'Rejected — future harvests of this sender are suppressed.'
          : `${ids.length} rejected — future harvests of these senders are suppressed.`,
        undoIds: ids,
      });
    },
    onError: (e, v, ctx) => { restorePending(e, v, ctx); showNotice(`Reject failed: ${e.message}`, { sticky: true }); },
    onSettled: refresh,
  });

  // Clear a whole low-signal tier. Server-side by tier (the queue is deeper
  // than one page); the button arms itself in the shared list, and the
  // returned ids feed one Undo — rejection is the harvest suppression list,
  // so it must stay recoverable.
  const rejectTier = useMutation({
    mutationFn: (t) => apiClient.post('/api/workspace/contact-candidates/reject-bulk', { tier: t }),
    onSuccess: (r) => {
      const ids = r?.ids || [];
      showNotice({
        text: `${r?.rejected ?? 0} rejected — future harvests of these senders are suppressed.`,
        undoIds: ids,
      });
      refresh();
    },
    onError: (e) => showNotice(`Bulk reject failed: ${e.message}`, { sticky: true }),
  });

  const unrejectBulk = useMutation({
    mutationFn: (ids) => apiClient.post('/api/workspace/contact-candidates/unreject-bulk', { ids }),
    onSuccess: (r) => showNotice(`${r?.unrejected ?? 0} restored to the pending queue.`),
    onError: (e) => showNotice(`Restore failed: ${e.message}`, { sticky: true }),
    onSettled: refresh,
  });

  const unreject = useMutation({
    mutationFn: (id) => apiClient.post(`/api/workspace/contact-candidates/${id}/unreject`),
    onMutate: (id) => {
      // The row leaves the rejected shelf NOW; refresh brings it back to
      // pending on settle.
      setRejected((a) => (a ? a.filter((c) => c.id !== id) : a));
    },
    onSuccess: () => showNotice('Restored to the pending queue.'),
    onError: (e) => { showNotice(`Restore failed: ${e.message}`, { sticky: true }); if (rejected !== null) loadRejected(); },
    onSettled: refresh,
  });

  const assignDomain = useMutation({
    mutationFn: ({ domain, target, org_type }) =>
      apiClient.post('/api/workspace/domains', { domain, ...target, org_type }),
    onSuccess: (r) => {
      showNotice(`Domain registered — ${r.retroMatched} contacts matched`);
      refresh();
    },
    onError: (e) => showNotice(`Domain assign failed: ${e.message}`, { sticky: true }),
  });

  if (isLoading) return <p className="aw-none">Loading contact inbox…</p>;
  if (error) return <p className="ci-error">Contact inbox unavailable: {error.message}</p>;

  const candidates = data?.candidates || [];

  return (
    <div className="ci-root">
      {notice && (
        <p className="ci-notice">
          {typeof notice === 'string' ? notice : notice.text}
          {typeof notice === 'object' && notice.undoIds && (
            <button className="ci-bulk-btn ci-notice-undo"
              onClick={() => {
                rejectBatch.current = { ids: [], at: 0 };
                const ids = notice.undoIds;
                if (ids.length === 1) unreject.mutate(ids[0]);
                else if (ids.length) unrejectBulk.mutate(ids);
              }}>
              Undo
            </button>
          )}
        </p>
      )}
      <ContactInboxList
        candidates={candidates}
        accounts={accounts}
        rejected={rejected}
        totalPending={data?.total}
        tiers={data?.tiers}
        tier={tier}
        onTierChange={setTier}
        onApprove={(id, overrides) => approve.mutate({ id, overrides })}
        onApproveBulk={(entries) => approveBulk.mutate(entries)}
        onReject={(id) => reject.mutate(id)}
        onUnreject={(id) => unreject.mutate(id)}
        onRejectTier={(t) => rejectTier.mutate(t)}
        onTeachDomain={(domain, target, org_type) => assignDomain.mutate({ domain, target, org_type })}
        onLoadRejected={loadRejected}
        emptyText="No contacts waiting for review. Run a Contact Radar scan in the GAS cockpit to harvest more."
      />
    </div>
  );
}

export function useContactInboxCount() {
  const { data } = useQuery({
    // Same key the unfiltered inbox uses, so the badge rides that cache.
    queryKey: ['contact-candidates', 'pending', 'all'],
    queryFn: () => apiClient.get('/api/workspace/contact-candidates?status=pending'),
    staleTime: 30_000,
  });
  // The list is capped server-side; the badge counts the whole queue.
  return data?.totalAll ?? data?.count ?? 0;
}
